import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { Form, Formik } from 'formik';
import { getIntegrationErrorMessage, POST_TEXT_MAX_LENGTH } from 'modules/integrations/domain/constants';
import { IntegrationStatus } from 'modules/integrations/domain/enums';
import {
  usePublishIntegrationPostMutation,
  useTelegramIntegrationQuery,
} from 'modules/integrations/store/tournament-integrations-api';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React from 'react';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import UDFormTextarea from 'ud-form/components/input/textarea';
import UDButton from 'ud-ui/components/button';
import UDLink from 'ud-ui/components/link';
import Modal from 'ud-ui/components/modal';
import UDText from 'ud-ui/components/text';
import * as yup from 'yup';

interface PublishPostModalProps {
  /** Id организации — backend `tournamentId`. */
  organizerId: number;
  /** Готовая картинка поста (из очереди медиа). */
  imageUrl: string;
  /** Подпись по умолчанию — текст, сгенерированный вместе с картинкой. */
  defaultText: string | null;
  onClose: () => void;
}

interface PublishPostFormValues {
  text: string;
}

const PUBLISH_FALLBACK = msg`Не удалось опубликовать пост. Попробуйте ещё раз.`;

/**
 * Публикация поста (картинка + подпись) в Telegram-канал организации. Если канал не
 * подключён или бот потерял доступ, вместо формы — объяснение и ссылка на настройки.
 */
const PublishPostModal = ({ organizerId, imageUrl, defaultText, onClose }: PublishPostModalProps) => {
  const { t } = useLingui();
  const { data: integration, isLoading, isError } = useTelegramIntegrationQuery(organizerId, { skip: !organizerId });
  const [publishPost] = usePublishIntegrationPostMutation();

  const isActive = integration?.status === IntegrationStatus.ACTIVE;
  const channelTitle = (integration?.channelUsername ? `@${integration.channelUsername}` : integration?.chatId) ?? '';

  const validationSchema = yup.object({
    text: yup.string().max(POST_TEXT_MAX_LENGTH, t`Подпись длиннее ${POST_TEXT_MAX_LENGTH} символов — Telegram её не примет`),
  });

  const handlePublish = async ({ text }: PublishPostFormValues) => {
    if (!integration) return;
    try {
      const result = await publishPost({ integrationId: integration.id, text, imageUrls: [imageUrl] }).unwrap();
      const postUrl = result.url;
      toast.success(
        postUrl ? (
          <span>
            <Trans>Пост опубликован.</Trans>{' '}
            <a href={postUrl} target="_blank" rel="noreferrer" className="underline">
              <Trans>Открыть в Telegram</Trans>
            </a>
          </span>
        ) : (
          t`Пост опубликован`
        ),
      );
      if (result.warning) {
        toast.warn(t`Картинка опубликована, а подпись — нет. Добавьте её в Telegram вручную.`);
      }
      onClose();
    } catch (error) {
      toast.error(t(getIntegrationErrorMessage(error, PUBLISH_FALLBACK)));
    }
  };

  const settingsHint = (
    <UDText type="subhead" className="text-inkSecondary block" data-testid="publish-post-not-connected">
      {integration ? (
        <Trans>Бот потерял доступ к каналу {channelTitle}. Верните ему права администратора или переподключите канал.</Trans>
      ) : (
        <Trans>Канал Telegram не подключён.</Trans>
      )}{' '}
      <UDLink to={Pages.SETTINGS.INTEGRATIONS} type="subhead" className="underline">
        <Trans>Открыть настройки интеграций</Trans>
      </UDLink>
    </UDText>
  );

  return (
    <Modal show onClose={onClose} size="md">
      <Modal.Header title={t`Публикация поста`} subtitle={isActive ? t`Telegram · ${channelTitle}` : undefined} />
      <Formik<PublishPostFormValues>
        initialValues={{ text: defaultText ?? '' }}
        validationSchema={validationSchema}
        onSubmit={handlePublish}
      >
        {({ isSubmitting }) => (
          <Form noValidate>
            <Modal.Body>
              <div className="flex flex-col gap-4">
                <img
                  src={imageUrl}
                  alt=""
                  className="rounded-card mx-auto max-h-[45vh] w-auto max-w-full"
                  data-testid="publish-post-image"
                />
                {isLoading ? (
                  <div className="flex-center text-inkSecondary py-4">
                    <ClipLoader color="currentColor" size={24} />
                  </div>
                ) : isError ? (
                  <UDText type="subhead" className="text-inkSecondary block" data-testid="publish-post-error">
                    <Trans>Не удалось проверить подключение к Telegram. Закройте окно и попробуйте снова.</Trans>
                  </UDText>
                ) : isActive ? (
                  <UDFormTextarea
                    name="text"
                    label={t`Подпись к посту`}
                    placeholder={t`Подпишите пост`}
                    rows={5}
                    // Общий стиль инпутов кита фиксирует высоту в одну строку — `min-height` её перекрывает.
                    className="min-h-32"
                    data-testid="publish-post-text"
                  />
                ) : (
                  settingsHint
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <UDButton variant="secondary" type="button" onClick={onClose}>
                <Trans>Отмена</Trans>
              </UDButton>
              {isActive && (
                <UDButton variant="primary" type="submit" loading={isSubmitting} data-testid="publish-post-submit">
                  <Trans>Опубликовать</Trans>
                </UDButton>
              )}
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default PublishPostModal;
